import type { UserRole, UserStatus } from './enums'

// Define interface for timezone entries
export interface TimezoneOption {
  value: string;
  label: string;
}

// Comprehensive list of timezones organized by region
export const ALL_TIMEZONES: TimezoneOption[] = [
  // North America
  { value: 'America/Anchorage', label: 'Alaska (AKST/AKDT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Arizona - no DST (MT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Halifax', label: 'Atlantic Time (AT)' },
  { value: 'America/St_Johns', label: 'Newfoundland Time (NT)' },
  
  // Central America & Caribbean
  { value: 'America/Mexico_City', label: 'Mexico City' },
  { value: 'America/Panama', label: 'Panama' },
  { value: 'America/Jamaica', label: 'Jamaica' },
  { value: 'America/Puerto_Rico', label: 'Puerto Rico' },
  
  // South America
  { value: 'America/Bogota', label: 'Colombia (COT)' },
  { value: 'America/Lima', label: 'Peru (PET)' },
  { value: 'America/Caracas', label: 'Venezuela (VET)' },
  { value: 'America/Santiago', label: 'Chile (CLT/CLST)' },
  { value: 'America/Sao_Paulo', label: 'Brazil - São Paulo (BRT)' },
  { value: 'America/Buenos_Aires', label: 'Argentina (ART)' },
  
  // Europe & Africa
  { value: 'Atlantic/Reykjavik', label: 'Iceland (GMT+0)' },
  { value: 'Europe/London', label: 'United Kingdom (GMT/BST)' },
  { value: 'Europe/Dublin', label: 'Ireland (GMT/IST)' },
  { value: 'Europe/Lisbon', label: 'Portugal (WET/WEST)' },
  { value: 'Europe/Paris', label: 'France (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Germany (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Spain (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Italy (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Netherlands (CET/CEST)' },
  { value: 'Europe/Brussels', label: 'Belgium (CET/CEST)' },
  { value: 'Europe/Zurich', label: 'Switzerland (CET/CEST)' },
  { value: 'Europe/Stockholm', label: 'Sweden (CET/CEST)' },
  { value: 'Europe/Oslo', label: 'Norway (CET/CEST)' },
  { value: 'Europe/Copenhagen', label: 'Denmark (CET/CEST)' },
  { value: 'Europe/Warsaw', label: 'Poland (CET/CEST)' },
  { value: 'Europe/Prague', label: 'Czech Republic (CET/CEST)' },
  { value: 'Europe/Vienna', label: 'Austria (CET/CEST)' },
  { value: 'Europe/Budapest', label: 'Hungary (CET/CEST)' },
  { value: 'Europe/Athens', label: 'Greece (EET/EEST)' },
  { value: 'Europe/Bucharest', label: 'Romania (EET/EEST)' },
  { value: 'Europe/Kiev', label: 'Ukraine (EET/EEST)' },
  { value: 'Europe/Moscow', label: 'Russia - Moscow (MSK)' },
  { value: 'Africa/Cairo', label: 'Egypt (EET)' },
  { value: 'Africa/Johannesburg', label: 'South Africa (SAST)' },
  { value: 'Africa/Lagos', label: 'Nigeria (WAT)' },
  { value: 'Africa/Nairobi', label: 'Kenya (EAT)' },
  
  // Asia & Middle East
  { value: 'Asia/Jerusalem', label: 'Israel (IST)' },
  { value: 'Asia/Dubai', label: 'United Arab Emirates (GST)' },
  { value: 'Asia/Baghdad', label: 'Iraq (AST)' },
  { value: 'Asia/Riyadh', label: 'Saudi Arabia (AST)' },
  { value: 'Asia/Tehran', label: 'Iran (IRST/IRDT)' },
  { value: 'Asia/Karachi', label: 'Pakistan (PKT)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Dhaka', label: 'Bangladesh (BST)' },
  { value: 'Asia/Bangkok', label: 'Thailand (ICT)' },
  { value: 'Asia/Kuala_Lumpur', label: 'Malaysia (MYT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Jakarta', label: 'Indonesia - Jakarta (WIB)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Taipei', label: 'Taiwan (CST)' },
  { value: 'Asia/Seoul', label: 'South Korea (KST)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  
  // Oceania
  { value: 'Australia/Perth', label: 'Australia - Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Australia - Adelaide (ACST/ACDT)' },
  { value: 'Australia/Darwin', label: 'Australia - Darwin (ACST)' },
  { value: 'Australia/Brisbane', label: 'Australia - Brisbane (AEST)' },
  { value: 'Australia/Sydney', label: 'Australia - Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Australia - Melbourne (AEST/AEDT)' },
  { value: 'Australia/Hobart', label: 'Australia - Hobart (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'New Zealand (NZST/NZDT)' },
  { value: 'Pacific/Fiji', label: 'Fiji (FJT/FJST)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
];

// Common languages for the dropdown
export interface LanguageOption {
  value: string;
  label: string;
}

export const COMMON_LANGUAGES: LanguageOption[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'ja', label: 'Japanese' },
  { value: 'zh', label: 'Chinese' },
];

// Common currencies for the dropdown
export interface CurrencyOption {
  value: string;
  label: string;
}

export const COMMON_CURRENCIES: CurrencyOption[] = [
  { value: 'USD', label: 'US Dollar ($)' },
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'GBP', label: 'British Pound (£)' },
  { value: 'JPY', label: 'Japanese Yen (¥)' },
  { value: 'CAD', label: 'Canadian Dollar (C$)' },
  { value: 'AUD', label: 'Australian Dollar (A$)' },
];

// Date formats
export interface DateFormatOption {
  value: string;
  label: string;
}

export const DATE_FORMATS: DateFormatOption[] = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
];

// Time formats
export interface TimeFormatOption {
  value: string;
  label: string;
}

export const TIME_FORMATS: TimeFormatOption[] = [
  { value: '12', label: '12-hour (AM/PM)' },
  { value: '24', label: '24-hour' },
];

// Base user type (excludes sensitive data)
export interface User {
  id: string
  email: string
  displayName: string | null
  firstName: string | null
  lastName: string | null
  emailVerified: Date | null
  phone: string | null
  phoneVerified: Date | null
  notificationSettings: NotificationSettings | null
  timezone: string | null
  language: string | null
  currency: string | null
  dateFormat: string | null
  timeFormat: string | null
  userSettings: UserSettings | null
  setupComplete: boolean
  image: string | null
  status: UserStatus
  role: UserRole
  createdAt: Date
  updatedAt: Date
}

// User settings types
export interface NotificationSettings {
  email: {
    enabled: boolean;
    frequency?: 'instant' | 'daily' | 'weekly';
    types?: {
      tasks?: boolean;
      threads?: boolean;
      mentions?: boolean;
      projects?: boolean;
    };
  };
  push: {
    enabled: boolean;
    types?: {
      tasks?: boolean;
      threads?: boolean;
      mentions?: boolean;
      projects?: boolean;
    };
  };
}

export interface UserSettings {
  theme?: 'light' | 'dark' | 'system';
  sidebar?: {
    collapsed?: boolean;
    favorites?: string[];
  };
  taskView?: {
    defaultView?: 'list' | 'board' | 'calendar';
    showCompleted?: boolean;
  };
}

// DTOs
export interface CreateUserDTO {
  email: string
  displayName: string
  firstName?: string
  lastName?: string
  role?: UserRole
  status?: UserStatus
  emailVerified?: Date | null
  cognitoSub?: string | null // Internal use only
  inviteToken?: string // Optional token for organization invitations
  captchaToken?: string
  setupComplete?: boolean
}

export interface UpdateUserDTO {
  displayName?: string | null
  firstName?: string | null
  lastName?: string | null
  image?: string | null
  role?: UserRole
  status?: UserStatus
  emailVerified?: Date | null
  cognitoSub?: string | null // Internal use only
  timezone?: string | null
  language?: string | null
  currency?: string | null
  dateFormat?: string | null
  timeFormat?: string | null
  notificationSettings?: any
  userSettings?: any
  setupComplete?: boolean
}

// Auth DTOs
export interface MagicLinkDTO {
  email: string
}

export interface MagicLinkVerifyDTO {
  token: string
}

export interface AuthResponse {
  accessToken: string
  refreshToken: string
  idToken: string
  expiresIn: number
}

export interface SessionResponse {
  success: boolean
  user: {
    email: string
    sub: string
  }
}

export interface RefreshTokenDTO {
  refreshToken: string;
}

// Profile DTOs
export interface UpdateProfileDTO {
  displayName?: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  department?: string;
  location?: string;
  phone?: string;
  bio?: string;
  timezone?: string;
  profilePicture?: string;
  setupComplete?: boolean;
  language?: string;
  currency?: string;
  dateFormat?: string;
  timeFormat?: string;
  notificationSettings?: any;
  userSettings?: any;
}
